const toNumber = (value, fallback = 0) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getSortedThresholds = (milestoneThresholds = []) => [...milestoneThresholds]
  .map((threshold) => ({
    ...threshold,
    bagNumber: toNumber(threshold?.bagNumber, 0),
  }))
  .sort((a, b) => a.bagNumber - b.bagNumber);

const getActiveMilestoneRange = ({
  milestoneThresholds = [],
  currentBagNumberInCycle = 0,
  nextMilestoneBagNumber = null,
}) => {
  const sortedThresholds = getSortedThresholds(milestoneThresholds);
  const thresholdCount = sortedThresholds.length;
  const safeCurrentBag = Math.max(0, toNumber(currentBagNumberInCycle, 0));

  if (!thresholdCount) {
    return {
      sortedThresholds,
      allMilestonesCompleted: false,
      activeThresholdIndex: -1,
      segmentStartBag: 0,
      showPerBagRewardAmount: true,
      useGroupedMarkers: false,
      previousMilestoneBagNumber: 0,
      previousMilestoneBonusAmount: 0,
      targetMilestoneBagNumber: 0,
      bagNumbersInRange: [],
      markerBagNumbers: [],
      completedBagCount: 0,
      progressPercent: 0,
    };
  }

  const safeNextMilestoneBagNumber = Number.isFinite(toNumber(nextMilestoneBagNumber, NaN))
    ? toNumber(nextMilestoneBagNumber, NaN)
    : NaN;
  const apiNextIndex = Number.isFinite(safeNextMilestoneBagNumber)
    ? sortedThresholds.findIndex(({ bagNumber }) => bagNumber === safeNextMilestoneBagNumber)
    : -1;
  const inferredNextIndex = sortedThresholds.findIndex(
    ({ bagNumber }) => bagNumber > safeCurrentBag,
  );
  const initialEffectiveNextIndex = apiNextIndex >= 0 ? apiNextIndex : inferredNextIndex;
  const shouldStartFromSecondMilestone = initialEffectiveNextIndex === 0
    && thresholdCount > 1
    && toNumber(sortedThresholds[0]?.bagNumber, 0) === 1
    && safeCurrentBag < 1;
  const effectiveNextIndex = shouldStartFromSecondMilestone ? 1 : initialEffectiveNextIndex;
  const allMilestonesCompleted = effectiveNextIndex === -1;
  const activeThresholdIndex = allMilestonesCompleted ? thresholdCount - 1 : effectiveNextIndex;

  const targetMilestoneBagNumber = toNumber(
    sortedThresholds[activeThresholdIndex]?.bagNumber,
    0,
  );
  const targetMilestoneBonusAmount = toNumber(
    sortedThresholds[activeThresholdIndex]?.bonusAmount,
    0,
  );
  const previousMilestoneBagNumber = activeThresholdIndex > 0
    ? toNumber(sortedThresholds[activeThresholdIndex - 1]?.bagNumber, 0)
    : 0;
  const previousMilestoneBonusAmount = activeThresholdIndex > 0
    ? toNumber(sortedThresholds[activeThresholdIndex - 1]?.bonusAmount, 0)
    : 0;
  const includePreviousMilestoneMarker = activeThresholdIndex > 0;
  const segmentStartBag = shouldStartFromSecondMilestone || includePreviousMilestoneMarker
    ? previousMilestoneBagNumber
    : previousMilestoneBagNumber + 1;
  const showPerBagRewardAmount = segmentStartBag < 10;
  const useGroupedMarkers = false;
  const segmentLength = Math.max(targetMilestoneBagNumber - segmentStartBag + 1, 1);
  const bagNumbersInRange = Array.from(
    { length: segmentLength },
    (_, index) => segmentStartBag + index,
  );
  const markerBagNumbers = bagNumbersInRange;

  const rawCompletedBags = allMilestonesCompleted
    ? segmentLength
    : safeCurrentBag - segmentStartBag + 1;
  const completedBagCount = clamp(rawCompletedBags, 0, segmentLength);
  const progressPercent = (completedBagCount / segmentLength) * 100;

  return {
    sortedThresholds,
    allMilestonesCompleted,
    activeThresholdIndex,
    segmentStartBag,
    showPerBagRewardAmount,
    useGroupedMarkers,
    previousMilestoneBagNumber,
    previousMilestoneBonusAmount,
    targetMilestoneBagNumber,
    targetMilestoneBonusAmount,
    bagNumbersInRange,
    markerBagNumbers,
    completedBagCount,
    progressPercent,
  };
};

export default getActiveMilestoneRange;
